import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { scrollToRef } from '../helpers'
import { getEnvVar } from '../helpers/environment'

const digitalPrescreenUrl = getEnvVar('DIGITAL_PRESCREEN_URL')

function Checkbox(props) {
  const { children, id, required } = props

  return (
    <>
      <svg
        width="16"
        height="14"
        viewBox="0 0 16 14"
        fill="none"
        className="checkbox-symbol"
        id="check"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15.7071 0.341709C16.0976 0.797321 16.0976 1.53601 15.7071 1.99162L5.70711 13.6583C5.31658 14.1139 4.68342 14.1139 4.29289 13.6583L0.292893 8.99163C-0.0976311 8.53601 -0.0976311 7.79732 0.292893 7.34171C0.683417 6.8861 1.31658 6.8861 1.70711 7.34171L5 11.1834L14.2929 0.341709C14.6834 -0.113903 15.3166 -0.113903 15.7071 0.341709Z"
          fill="#fff"
        />
      </svg>

      <span className="checkbox-container">
        <input className="checkbox-input" id={id} name={id} type="checkbox" required={required} />
        <label className="checkbox" htmlFor={id}>
          <span className="checkbox-img">
            <svg width="16px" height="14px">
              <use xlinkHref="#check" />
            </svg>
          </span>
          <span className="checkbox-text">{children}</span>
        </label>
      </span>
    </>
  )
}

Checkbox.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  required: PropTypes.bool
}

Checkbox.defaultProps = {
  required: false
}

async function postWaitlistData(formData = {}) {
  const API = axios.create({
    baseURL: digitalPrescreenUrl,
    withCredentials: true
  })

  try {
    const { data } = await API.get('/token')

    const config = {
      headers: { Authorization: `Bearer ${data.token}` }
    }

    const response = await API.post('/signup', formData, config)
    return response
  } catch (error) {
    return { error: error?.response?.data?.error || 'Unknown' }
  }
}

function MLSWaitlistForm() {
  const [submitted, setSubmitted] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const mlsCompletedRef = useRef()

  useEffect(() => {
    if (completed) {
      scrollToRef(mlsCompletedRef, 'center')
    }
  }, [completed])

  const handleFormSubmit = async event => {
    setSubmitted(true)
    setError(false)
    const form = event.target.parentNode

    if (form.checkValidity()) {
      setLoading(true)

      const formData = new FormData(form)
      const userData = {
        co_brand: 'MLS',
        product_name: 'MLS Forward Credit Card',
        product_type: 'card'
      }
      const inputFields = [...formData.entries()]
      inputFields.forEach(data => {
        const [key, value] = data
        userData[key] = value
      })

      const waitlistUserData = {
        co_brand: userData.co_brand,
        product_name: userData.product_name,
        product_type: userData.product_type,
        email: userData.email,
        zip_code: userData.postalCode,
        city: userData.city,
        state: userData.state,
        street: userData.addressLine1
      }

      const identifyUserData = {
        email: userData.email,
        city: userData.city,
        state: userData.state,
        co_brand: userData.co_brand,
        first_name: userData.firstName,
        last_name: userData.lastName
      }
      const response = await postWaitlistData(Object.fromEntries(formData))

      if (response.error) {
        setError(response.error)
      } else {
        setCompleted(true)
        if (window.analytics) {
          window.analytics.identify(identifyUserData)
          window.analytics.track('Waitlist Enrolled', waitlistUserData)
        }
      }

      setLoading(false)
    }
  }

  if (completed) {
    return (
      <div className="mls-completed" ref={mlsCompletedRef}>
        <p>
          Thank you in advance for joining the waitlist for the MLS Forward Credit Card powered by
          Avant.
        </p>

        <p>You’ll be notified by email when you can apply for the card designed for MLS winners.</p>
      </div>
    )
  }

  return (
    <form className={`mls-form${submitted ? ' submitted' : ''}`}>
      <h4>Your information</h4>
      <div className="form-block x-space">
        <label>
          First Name*
          <input type="text" name="firstName" pattern=".*\S+.*" required />
          <div className="required-label">This is a required field.</div>
        </label>
        <label>
          Last Name*
          <input type="text" name="lastName" pattern=".*\S+.*" required />
          <div className="required-label">This is a required field.</div>
        </label>
      </div>
      <div className="form-block x-space">
        <label>
          Email Address*
          <input type="email" name="email" required />
          <div className="required-label">This is a required field.</div>
        </label>
      </div>
      <div className="form-checkmark">
        <Checkbox id="privacyConsent" required>
          I read, understand, and consent to:{' '}
          <a href="https://www.avant.com/privacy/">Avant’s Privacy Notice</a> and{' '}
          <a href="https://www.avant.com/webbank-privacy-notice/">Webbank’s Privacy Notice</a>.
        </Checkbox>
      </div>
      <h4>Address</h4>
      <div className="form-block small-right">
        <label className="block-large">
          Street*
          <input type="text" name="addressLine1" required />
          <div className="required-label">This is a required field.</div>
        </label>
        <label>
          Unit
          <input type="text" name="addressLine2" />
          <div className="required-label">This is a required field.</div>
        </label>
      </div>
      <div className="form-block">
        <div className="form-block block-large">
          <label className="block-x-large">
            City*
            <input type="text" name="city" required />
            <div className="required-label">This is a required field.</div>
          </label>
          <label>
            State*
            <input type="text" name="state" minLength="2" maxLength="2" required />
            <div className="required-label">This is required.</div>
          </label>
        </div>
        <label>
          Zipcode*
          <input
            type="text"
            name="postalCode"
            inputMode="numeric"
            minLength="5"
            maxLength="10"
            pattern="^[0-9]{5}|[0-9]{5}-[0-9]{4}|[0-9]{9}$"
            title="NNNNN or NNNNN-NNNN"
            required
          />
          <div className="required-label">This is a required field.</div>
        </label>
      </div>
      {error && <div className="form-error">An error occurred ({error}). Please try again.</div>}
      <button type="submit" disabled={loading} onClick={handleFormSubmit}>
        {loading ? 'Submitting data...' : 'Join the waitlist'}
      </button>
    </form>
  )
}

export default MLSWaitlistForm
